import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { ayreneService, getTokenExpiration } from './ayrene.service';

const AYRENE_API_BASE_URL = 'https://api.ayrene.com/api';

const ayreneAxios = axios.create({
    baseURL: AYRENE_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

ayreneAxios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('ayreneAuthToken');

        if (token) {
            const expiration = getTokenExpiration(token);
            const now = Date.now();

            if (expiration && expiration - now < 2 * 60 * 1000) {
                console.log('🔄 Token expiring soon, refreshing proactively...');

                if (!isRefreshing) {
                    isRefreshing = true;

                    try {
                        const result = await ayreneService.refreshToken();
                        if (result.success && result.accessToken) {
                            processQueue(null, result.accessToken);
                            config.headers.Authorization = `Bearer ${result.accessToken}`;
                        } else {
                            processQueue(new Error('Token refresh failed'), null);
                        }
                    } catch (error) {
                        processQueue(error, null);
                    } finally {
                        isRefreshing = false;
                    }
                } else {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then((token) => {
                            config.headers.Authorization = `Bearer ${token}`;
                            return config;
                        })
                        .catch((error) => {
                            return Promise.reject(error);
                        });
                }
            } else {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

ayreneAxios.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return ayreneAxios(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const result = await ayreneService.refreshToken();

                if (result.success && result.accessToken) {
                    processQueue(null, result.accessToken);

                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
                    }

                    return ayreneAxios(originalRequest);
                } else {
                    processQueue(new Error('Token refresh failed'), null);

                    if (typeof window !== 'undefined') {
                        localStorage.removeItem('ayreneAuthToken');
                        localStorage.removeItem('ayreneRefreshToken');
                        localStorage.removeItem('ayreneTokenExpiration');
                        localStorage.removeItem('ayreneLoginStatus');
                        window.location.href = '/login';
                    }

                    return Promise.reject(error);
                }
            } catch (refreshError) {
                processQueue(refreshError, null);

                if (typeof window !== 'undefined') {
                    localStorage.removeItem('ayreneAuthToken');
                    localStorage.removeItem('ayreneRefreshToken');
                    localStorage.removeItem('ayreneTokenExpiration');
                    localStorage.removeItem('ayreneLoginStatus');
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default ayreneAxios;
